<template>
  <component
    :is="getFavoritesComponent"
    :title="`${t('Public.Favourites')}`"
    :classes="['bto-sb-widget-myfavourites']">
    <inline-message
      v-show="!fav.length && !favCategories.length && !favTournaments.length"
      type="info"
      :message="t('Mobile.NoFavoritesMessage')" />
    <ul>
      <li
        class="bto-sb-sports-icon"
        v-for="(match, idx) in fav.slice(0, numberOfFavItems || DEFAULT_MAX_ITEMS)"
        :class="getSportClass(match.sportId)"
        :key="idx">
        <span class="bto-sb-myfavourites-item">
          <router-link :to="openDetails(match)">
            <span class="bto-sb-myfavourites-match">
              {{ match.homeTeam }} - {{ match.guestTeam }}
            </span>
            <span
              v-if="match.isLiveMatch && (match.time !== '' || match.time !== null)"
              class="bto-sb-myfavourites-match-live">
              {{ match.score.home }} : {{ match.score.away }} {{ match.data.ms }} /
              {{ match.time || "--" }}'
            </span>
            <span v-else class="bto-sb-myfavourites-match-date">
              {{ match.date }} {{ match.time }}
            </span>
          </router-link>
        </span>
        <span class="bto-sb-myfavourites-remove">
          <a href="#" @click.prevent="removeFromFavorite(match.matchId)" :title="t('W.RemFav')"></a>
        </span>
      </li>
      <ul v-for="(sport, index) in groupedBySport" :key="`sport-${index}`">
        <li
          class="bto-sb-sports-icon"
          v-for="(tournament, tournamentIndex) in sport.tournaments" :key="`tournament-${tournamentIndex}`"
          :class="getSportClass(tournament.sportId)">
          <span class="bto-sb-myfavourites-item tournament">
            <router-link :to="openTournament(tournament)">
              <span class="bto-sb-myfavourites-match">
                {{ tournament.tournament }}
              </span>
            </router-link>
          </span>
          <span class="bto-sb-myfavourites-remove">
            <a
              href="javascript:void(0);"
              @click.prevent="removeFavTournament(tournament)"
              :title="t('W.RemFav')"></a>
          </span>
        </li>
        <li
          class="bto-sb-sports-icon"
          v-for="(category, categoryIndex) in sport.categories" :key="`category-${categoryIndex}`"
          :class="getSportClass(category.sportId)">
          <span class="bto-sb-myfavourites-item cat">
            <router-link :to="openCategory(category)">
              <span class="bto-sb-myfavourites-match">
                {{ category.category }}
              </span>
            </router-link>
          </span>
          <span class="bto-sb-myfavourites-remove">
            <a
              href="javascript:void(0);"
              @click.prevent="removeFavCategory(category)"
              :title="t('W.RemFav')"></a>
          </span>
        </li>
      </ul>
    </ul>
  </component>
</template>

<script>
  import store from '../../../store';
  import { LiveWorker } from '../../../workers';
  import Accordion from '../../shared/accordion';
  import ItemList from '../../shared/item-list.vue'
  import LiveMatchModel from '../live-now/models/live-match-model';
  import { urlFriendlyParam } from '@bto-services/utils';

  export default {
    name: 'MyFavoritesWidget',
    data() {
      return {
        expanded: true,
        liveAll: {},
        DEFAULT_MAX_ITEMS: 5
      };
    },
    props: {
      numberOfFavItems: {
        label: 'Number of favorite items to show',
        type: Number,
        default: 5,
        description: 'Max number of items to be displayed in the favorites widget. Defaults to 5',
      },
    },
    created() {
      this.dispatch();
      // get allLiveMatches from signalR on each ping of "availableMatches"
      LiveWorker.addEventListener('message', (msg) => {
        if (msg.data.type === 'liveall') {
          this.liveAll = msg.data.data.lm;
        }
      });
    },
    computed: {
      allFav() {
        return store.state.favoritesModule.favorites;
      },
      favCategories() {
        return store.state.favoritesModule.categories || [];
      },
      favTournaments() {
        return store.state.favoritesModule.tournaments || [];
      },
      // raw signalR data from last recorded ping of "availableMatches"
      availableLive() {
        return window.btoSignalRAvailibility;
      },
      fav() {
        // live favorites
        let lf = [];
        // prematch favorites
        const pf = store.state.favoritesModule.favoriteMatches;
        // map the data for live favorite events
        this.allFav.forEach((m) => {
          let event = null;
          if (m.isLive) {
            // if no data from liveWorker check for raw signalR data
            if (!Object.keys(this.liveAll).length && this.availableLive) {
              event = this.availableLive.find(lm => lm.brid === m.betRadarId);
              if (event) {
                event = new LiveMatchModel(event, APP_SETTINGS);
              }
            }
            else if (this.liveAll.hasOwnProperty(m.sportId) && this.liveAll[m.sportId].hasOwnProperty(m.betRadarId)) {
              event = this.liveAll[m.sportId][m.betRadarId];
            }
            if (event) {
              lf.push(event);
            }
          }
        });
        return [...lf, ...pf];
      },
      getFavoritesComponent() {
        if (APP_SETTINGS.settings.layout === 'US') {
          return ItemList;
        }
        return Accordion;
      },
      /**
      * returns groups by sports of tournaments and categories based on "numberOfFavItems" cdn widget settings 
      */
      groupedBySport() {
        let maxItems = this.numberOfFavItems || this.DEFAULT_MAX_ITEMS;
        if (this.fav.length > maxItems) { return [] }
        let itemCount = 0;
        maxItems -= this.fav.length;
        return this.getAllSports().map(sport => {
          const tournaments = this.getAllMatchesBySport(sport.id, this.favTournaments).filter(() => {
            const includeItem = itemCount < maxItems;
            if (includeItem) { itemCount++; }
            return includeItem;
          });
          
          const categories = this.getAllMatchesBySport(sport.id, this.favCategories).filter(() => {
            const includeItem = itemCount < maxItems;
            if (includeItem) { itemCount++; }
            return includeItem;
          });

          return {
            ...sport,
            tournaments,
            categories
          };
        }).filter(sport => sport.tournaments.length > 0 || sport.categories.length > 0);
      }
    },
    methods: {
      getAllMatches() {
        return [...this.allFav, ...this.fav, ...this.favTournaments, ...this.favCategories];
      },
      getAllSports() {
        return this.getAllMatches().reduce((acc, current) => {
          const x = acc.find(item => item.id === current.sportId);
          if (!x) {
            acc.push({ id: current.sportId });
          }
          return acc;
        }, []);
      },
      getAllMatchesBySport(id, matches) {
        return matches.filter(item => item.sportId === id);
      },
      removeFromFavorite(a) {
        store.commit('removeFavorite', a);
      },
      getSportClass(sportId) {
        return store.getters.getSportClass(sportId);
      },
      openDetails(match) {
        const rawSportName = store.getters.getSportName(match.sportId);
        const sportName = urlFriendlyParam(rawSportName);
        const categoryName = urlFriendlyParam(match.tournamentName);
        const eventName = urlFriendlyParam(`${match.homeTeam} vs ${match.guestTeam}`);
        if (match.isLiveMatch) {
          return { name: 'seoGameLive', params: { sportName, categoryName, eventName, matchId: match.betRadarId } };
        }
        return { name: 'seoGame', params: { sportName, categoryName, eventName, matchId: match.matchId } };
      },
      openCategory(c) {
        const sport = urlFriendlyParam(c.sport);
        const category = urlFriendlyParam(c.category);
        const categoryId = `${c.categoryId}`;
        const routeName = c.cType === 2 ? 'seoCategoryGroup' : 'seoCategory';
        return { name: routeName, params: { sport, category, categoryId } };
      },
      openTournament(t) {
        const sport = urlFriendlyParam(t.sport);
        const tournament = urlFriendlyParam(t.tournament);
        const fixtureIds = `${t.tournamentId}`;
        const routeName = t.cType === 4 ? 'seoTournamentGroup' : 'seoTournament';
        return { name: routeName, params: { sport, tournament, fixtureIds } };
      },
      removeFavTournament(t) {
        this.$store.commit('removeFavoriteTournament', `${t.tournamentId}`);
      },
      removeFavCategory(c) {
        this.$store.commit('removeFavoriteCategory', `${c.categoryId}`);
      },
      dispatch() {
        store.dispatch('cleanOldFavorites');
        store.dispatch('getFavoriteMatches');
      },
      toggleWidget() {
        this.expanded = !this.expanded;
      },
    },
    components: {
      Accordion,
      ItemList
    }
  };
</script>
